import * as z from 'zod'; // Parcel issue https://github.com/colinhacks/zod/issues/4923

export const UserSchema = z.object({
  aaid: z.string(),
  staffId: z.string(),
  name: z.string(),
});

export const TicketStatusSchema = z.enum([
  'READY',
  'DONE',
  'COMPLETE',
  'IN_PROGRESS',
  'BLOCKED',
]);

// Supported product keys in Release Management Service
export const ProductSchema = z.enum([
  'access',
  'assets',
  'automation',
  'cmdb',
  'atlassian-together',
  'avocado',
  'avp',
  'beacon',
  'bitbucket',
  'canvas',
  'cloud-admin',
  'compass',
  'confluence',
  'devai',
  'feedback',
  'goal',
  'guard',
  'hipchat.cloud',
  'jira-admin',
  'jira-align',
  'jira-core',
  'jira-customer-service',
  'jira-family',
  'jira-incident-manager',
  'jira-product-discovery',
  'jira-servicedesk',
  'jira-servicedesk.ondemand',
  'jira-software',
  'jira-work-management',
  'loom',
  'loom-attribution',
  'mercury',
  'opsgenie',
  'passionfruit',
  'project',
  'radar',
  'refapp',
  'rovo',
  'statuspage',
  'studio',
  'teams',
  'townsquare',
  'trello',
  'unknown-atlassian',
]);

export const FeaturedImageSchema = z.object({
  url: z.string(),
  altTag: z.string(),
  title: z.string(),
  width: z.number(),
  height: z.number(),
});

export const UpdateSchema = z.object({
  id: z.string(),
  title: z.string(),
  products: z.array(ProductSchema).optional().nullable(),
  status: z
    .enum([
      'COMING_SOON',
      'PLANNED',
      'ROLLING_OUT',
      'GENERALLY_AVAILABLE',
      'ROLLED_BACK',
    ])
    .optional()
    .nullable(),
  type: z
    .enum(['IMPROVEMENT', 'FIX', 'EXPERIMENT', 'REMOVED', 'ANNOUNCEMENT'])
    .optional()
    .nullable(),
  // AI-generated impact rating for the update
  impactRating: z.enum(['HIGH', 'MEDIUM', 'LOW']).optional().nullable(),
  // AI-generated contextual tags replacing change type
  tags: z.array(z.string()).optional().nullable(),
  // List and details page fields
  summary: z.string().optional().nullable(),
  releaseStartDate: z.string().optional().nullable(), // ISO date string
  releaseEndDate: z.string().optional().nullable(), // ISO date string
  firstPublishedAt: z.string().optional().nullable(), // ISO date string
  // Details page fields
  getStarted: z.string().optional().nullable(), // Atlassian Document Format (JSON)
  keyChanges: z.string().optional().nullable(), // Atlassian Document Format (JSON)
  benefitsList: z.string().optional().nullable(), // Atlassian Document Format (JSON)
  reasonForChange: z.string().optional().nullable(), // Atlassian Document Format (JSON)
  prepareForChange: z.string().optional().nullable(), // Atlassian Document Format (JSON)
  featuredImage: FeaturedImageSchema.optional().nullable(),
});

export const UpdatesLinksSchema = z.object({
  prev: z.string().nullable(),
  self: z.string(),
  next: z.string().nullable(),
});
// Hides items that fail validation, so one unsupported change can't fail the
// whole list (TC-11238).
// TODO: TC-11240 — render them instead. Hidden items aren't reported anywhere
// and still count towards meta.total.
const hidingUnsupportedItems = <Schema extends z.ZodType>(itemSchema: Schema) =>
  z
    .array(itemSchema.nullable().catch(null))
    .transform((items) =>
      items.filter((item): item is z.infer<Schema> => item !== null),
    );

export const UpdatesSchema = hidingUnsupportedItems(UpdateSchema);
export type UpdatesLinks = z.infer<typeof UpdatesLinksSchema>;

export const UpdatesResponseSchema = z.object({
  data: UpdatesSchema,
  links: UpdatesLinksSchema,
  meta: z.object({ total: z.number() }).optional().nullable(),
});

export type UpdatesResponseType = z.infer<typeof UpdatesResponseSchema>;

// Bundle changes extend a regular update with preview/bundled release track dates.
export const BundleChangeSchema = UpdateSchema.extend({
  previewTrackReleaseDate: z.string().optional().nullable(),
  bundledTrackReleaseDate: z.string().optional().nullable(),
});

export const BundleChangesSchema = hidingUnsupportedItems(BundleChangeSchema);

export const BundleChangesResponseSchema = z.object({
  data: BundleChangesSchema,
  links: UpdatesLinksSchema,
  meta: z.object({ total: z.number() }).optional().nullable(),
});

export type BundleChangeType = z.infer<typeof BundleChangeSchema>;
export type BundleChangesResponseType = z.infer<
  typeof BundleChangesResponseSchema
>;

export const MetricWithChangeSchema = z.object({
  currentValue: z.number().nullable(),
  percentageChange: z.number().nullable(),
});

export const UpdateMetricsSchema = z.object({
  totalActiveUpdates: MetricWithChangeSchema,
  awaitingApproval: MetricWithChangeSchema,
  approvedForUpdate: MetricWithChangeSchema,
  averageApprovalTimeHours: MetricWithChangeSchema,
});

// Type exports
export type UserDTO = z.infer<typeof UserSchema>;
export type TicketStatus = z.infer<typeof TicketStatusSchema>;
export type FeaturedImageType = z.infer<typeof FeaturedImageSchema>;
export type UpdateType = z.infer<typeof UpdateSchema>;
export type UpdatesType = z.infer<typeof UpdatesSchema>;
export type MetricWithChange = z.infer<typeof MetricWithChangeSchema>;
export type UpdateMetrics = z.infer<typeof UpdateMetricsSchema>;
